"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./TemplatePreSelectModal.module.css";

/**
 * TemplatePreSelectModal — Phase 25-AB Task 18 #10 (panel 63 / wc-tps-modal).
 *
 * Shown after PreAnalysisModal proceeds. Lists 100+ templates pre-grouped by
 * detected genre (vlog / news / review / education / etc). Default selection
 * is the AI-recommended template; user can override or skip templates entirely.
 *
 * Mockup source: docs/mockups/welcome.html lines 1019-1041.
 */
export interface TemplateCard {
	id: string;
	label: string;
	thumbBg?: string;
}

export interface TemplatePreSelectModalProps {
	open: boolean;
	templates?: TemplateCard[];
	recommendedGenre?: string;
	onClose: () => void;
	onSelect?: (templateId: string | null) => void;
	onSeeAll?: () => void;
}

const DEFAULT_TEMPLATES: TemplateCard[] = [
	{ id: "vlog-sensual", label: "감성 vlog" },
	{
		id: "travel-hooks",
		label: "여행 hooks",
		thumbBg: "linear-gradient(135deg,#7a6a4a,#3a2a1a)",
	},
	{
		id: "daily-cute",
		label: "일상 cute",
		thumbBg: "linear-gradient(135deg,#6a8a6a,#3a5a3a)",
	},
	{
		id: "minimal",
		label: "미니멀",
		thumbBg: "linear-gradient(135deg,#a3a3a3,#444)",
	},
	{
		id: "cinema",
		label: "시네마",
		thumbBg: "linear-gradient(135deg,#c9b3ff,#5a3b9c)",
	},
	{
		id: "energy",
		label: "에너지",
		thumbBg: "linear-gradient(135deg,#ffaa5b,#cc4a14)",
	},
	{
		id: "news",
		label: "뉴스 / 정보",
		thumbBg: "linear-gradient(135deg,#3a5a8e,#1a2a4e)",
	},
	{
		id: "review",
		label: "리뷰 / 언박싱",
		thumbBg: "linear-gradient(135deg,#a05a1a,#6a3a0a)",
	},
	{
		id: "education",
		label: "교육",
		thumbBg: "linear-gradient(135deg,#22c55e,#0a7a3e)",
	},
	{
		id: "game-tech",
		label: "게임 / 테크",
		thumbBg: "linear-gradient(135deg,#4eb3c1,#2a7a8a)",
	},
];

export function TemplatePreSelectModal({
	open,
	templates = DEFAULT_TEMPLATES,
	recommendedGenre = "vlog",
	onClose,
	onSelect,
	onSeeAll,
}: TemplatePreSelectModalProps) {
	const [mounted, setMounted] = useState(false);
	const [selected, setSelected] = useState<string | null>(
		templates[0]?.id ?? null,
	);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	if (!open || !mounted) return null;

	return createPortal(
		<div
			id="wc-tps-modal"
			className={styles.overlay}
			role="dialog"
			aria-modal="true"
			aria-label="템플릿 선택"
			{...devLabelPanel("63 · TemplatePreSelectModal")}
		>
			<div className={styles.card}>
				<div className={styles.head}>
					<div className={styles.title}>템플릿 선택</div>
					<button
						type="button"
						className={styles.close}
						onClick={onClose}
						aria-label="닫기"
					>
						×
					</button>
				</div>
				<div className={styles.hint}>
					<strong className={styles.hintHi}>AI 추천</strong> (장르:{" "}
					{recommendedGenre} 매칭) · 또는 전체 100+ 직접 선택
				</div>
				<div className={styles.grid} {...devLabel("63-A")} data-testid="template-grid">
					{templates.map((t) => {
						const on = selected === t.id;
						return (
							<button
								key={t.id}
								type="button"
								onClick={() => setSelected(t.id)}
								className={on ? styles.itemOn : styles.item}
								data-template-id={t.id}
								aria-pressed={on}
							>
								<div
									className={styles.thumb}
									style={t.thumbBg ? { background: t.thumbBg } : undefined}
								/>
								<div className={styles.itemLabel}>{t.label}</div>
							</button>
						);
					})}
				</div>
				<div className={styles.actions}>
					<button
						type="button"
						className={styles.btnSec}
						onClick={onSeeAll}
						{...devLabel("63-B")}
					>
						전체 100+ 보기
					</button>
					<button
						type="button"
						className={styles.btnGhost}
						onClick={() => {
							onSelect?.(null);
							onClose();
						}}
						{...devLabel("63-C")}
					>
						템플릿 없이
					</button>
					<button
						type="button"
						className={styles.btnPrimary}
						onClick={() => {
							onSelect?.(selected);
							onClose();
						}}
						{...devLabel("63-D")}
					>
						분석 시작 (12코인)
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
}
